import React, { useState, useRef } from 'react';
import { FileParseError, ERROR_TYPES } from '../utils/fileParser';

const FileUploadModal = ({ isOpen, onClose, onNext, onBack, externalError, onClearError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null); // Internal error state
  const fileInputRef = useRef(null);

  // Use external error if provided, otherwise use internal error
  const displayError = externalError || error;

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelection = (file) => {
    setError(null);
    if (onClearError) onClearError();
    
    // Check file type
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/vcard',
      'text/x-vcard'
    ];
    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['csv', 'xls', 'xlsx', 'vcf'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setError({
        type: ERROR_TYPES.UNSUPPORTED_FORMAT,
        message: `File format ".${fileExtension}" is not supported`,
        suggestions: [
          'Please use CSV, Excel (.xls/.xlsx), or vCard (.vcf) files',
          'Export your contacts from your address book or spreadsheet application',
          'Make sure the file extension matches the actual file format'
        ]
      });
      return;
    }
    
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError({
        type: ERROR_TYPES.CORRUPTED_FILE,
        message: 'File size must be less than 10MB',
        suggestions: [
          'Try uploading a smaller file with fewer members',
          'Split your contacts into multiple smaller files',
          'Remove any unnecessary columns from your spreadsheet'
        ]
      });
      return;
    }
    
    // Check if file is empty
    if (file.size === 0) {
      setError({
        type: ERROR_TYPES.EMPTY_FILE,
        message: 'Selected file is empty',
        suggestions: [
          'Make sure your file contains member data',
          'Check that the file was properly exported',
          'Try re-downloading or re-exporting the file'
        ]
      });
      return;
    }
    
    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleNext = () => {
    if (selectedFile) {
      onNext(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    if (onClearError) onClearError();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Upload File</h2>
          <button 
            className="text-gray-400 hover:text-gray-600 text-3xl font-light leading-none transition-colors"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-4 text-gray-700">
            <p>Upload your members file (CSV, Excel, or vCard format)</p>
          </div>

          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
              dragActive 
                ? 'border-indigo-500 bg-indigo-50' 
                : selectedFile 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">📄</div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{selectedFile.name}</div>
                    <div className="text-sm text-gray-600">{(selectedFile.size / 1024).toFixed(1)} KB</div>
                  </div>
                </div>
                <button 
                  className="text-gray-400 hover:text-red-600 text-2xl font-light leading-none transition-colors"
                  onClick={handleRemoveFile}
                >
                  ×
                </button>
              </div>
            ) : (
              <div>
                <div className="text-5xl mb-3">📁</div>
                <p className="text-gray-700 mb-2">Drag and drop your file here</p>
                <p className="text-gray-500 text-sm mb-4">or</p>
                <button 
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all hover:-translate-y-px hover:shadow-lg"
                  onClick={handleBrowseClick}
                >
                  Browse Files
                </button>
              </div>
            )}
          </div>

          {displayError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-xl">⚠️</span>
                <span className="font-semibold text-red-900">{displayError.message}</span>
              </div>
              {displayError.suggestions && displayError.suggestions.length > 0 && (
                <div className="ml-7">
                  <div className="text-sm font-medium text-red-800 mb-2">How to fix this:</div>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {displayError.suggestions.map((suggestion, index) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx,.vcf"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-gray-700">
            <p>Members will be added to your community on the Yesod app.</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button 
            className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all hover:-translate-y-px"
            onClick={onBack}
          >
            Back
          </button>
          <button 
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all hover:-translate-y-px hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            onClick={handleNext}
            disabled={!selectedFile}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;